import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { EventEmitter, Injectable, Output } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms'
import { catchError, throwError } from 'rxjs'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Player } from '../../../core/models/player.model'
import { environment } from '../../../../environments/environment'
import { Position, lineups } from '../../../core/models/positions.model'

export interface PositionKey {
  team: 'for' | 'against'
  positionId: number
}

@Injectable({
  providedIn: 'root'
})
export class LineupService {
  readonly baseURL = environment.DOMAIN_URL+'/api/matches/'

  @Output() screenshotEvent = new EventEmitter<void>()
  Screenshot() {
    this.screenshotEvent.emit()
  }

  @Output() playerAddedEvent = new EventEmitter<PositionKey>()
  PlayerAdded(positionId: number, team: 'for' | 'against') {
    this.playerAddedEvent.emit({ team, positionId })
  }

  formMap = {
    for: this.fb.group({
      formation: ['3-4-2-1'],
      name: [''],
      badge: [true],
      sub: [true],
    }),
    against: this.fb.group({
      formation: ['3-4-2-1'],
      name: [''],
      badge: [true],
      sub: [true],
    })
  }

  positionsMap: Record<'for' | 'against', Position[]> = {
    for: [],
    against: []
  }

  playerForm = this.fb.group({
    player: this.fb.control<Player | null>(null, [Validators.required, this.requireMatch.bind(this)]),
    player_name: [''],
    sub_name: [''],
    sub_player: this.fb.control<Player | null>(null)
  })

  priceForm = this.fb.group({
    player: this.fb.control<Player | null>(null),
    price: this.fb.control(0, [Validators.required, Validators.min(0), Validators.max(300)]),
  })

  players: Player[] = []

  constructor(
    private _snackBar: MatSnackBar,
    private fb: FormBuilder,
    private http: HttpClient
  ) {}

  private requireMatch(control: FormControl): ValidationErrors | null {
    const selection = control.value
    if (this.players && this.players.indexOf(selection) < 0 && selection !== '') {
      return { requireMatch: true }
    }
    return null
  }

  loadPositions(team: 'for' | 'against') {
    if (!this.positionsMap[team].length) {
      const lineup = lineups['3-4-2-1']
      this.positionsMap[team] = lineup.map(pos => ({ ...pos }))

      this.getForm(team).controls['formation'].valueChanges.subscribe(val => {
        if (val) this.updatePositions(lineups[val as keyof typeof lineups], team)
      })
    }
  }

  updatePositions(lineup: Position[], team: 'for' | 'against') {
    lineup.forEach(newPos => {
      const existing = this.positionsMap[team].find(p => p.starterPositionId === newPos.starterPositionId)
      if (existing) {
        existing.x = newPos.x
        existing.y = newPos.y
      }
    })
  }

  addPlayerToPosition(team: 'for' | 'against', positionId: number) {
    this.playerAddedEvent.emit({ team, positionId })
  }

  getForm(team: 'for' | 'against') {
    return this.formMap[team]
  }

  getPositions(team: 'for' | 'against') {
    return this.positionsMap[team]
  }

  patchMatchLineup(team: 'for' | 'against', matchId: string) {
    const form = this.getForm(team) as FormGroup
    const payload = {
      teamSide: team,
      formation: form.get('formation')?.value,
      positions: this.getPositions(team)
      .filter(pos => !!pos.player)
      .map(p => ({
        player_id: p.player?._id,
        starterPositionId: p.starterPositionId,
        generalPosition: p.generalPosition,
        specificPosition: p.specificPosition
      }))
    }

    return this.http.patch(this.baseURL + matchId + '/lineup', payload)
    .pipe(catchError(this.errorHandler))
  }

  private errorHandler(error: HttpErrorResponse) {
    console.error('Error:', error)
    return throwError(() => new Error('An error occurred; please try again later.'))
  }

  openSnackBar(message: string) {
    this._snackBar.open(message)
  }
}